import { useRatingControllerGetProductRatingHistory } from "@/api/types/main";
import {
  NumberCeilDTO,
  NullableNumberCeilDTO,
  GetProductRatingHistoryResponseDTO,
} from "@/api/types/main/schemas";
import { LoadingSpinner } from "@/components/custom-ui/loading.spinner";
import { TanstackTable } from "@/components/custom-ui/tables/tanstack.table";
import {
  MemoizedTanstackTableBody,
  TanstackTableBody,
} from "@/components/custom-ui/tables/tanstack.table.body";
import {
  MemoizedTanstackTableHeader,
  TanstackTableHeader,
} from "@/components/custom-ui/tables/tanstack.table.header";
import { DndSortableHeadersContext } from "@/components/tables/dnd.sortable.headers.context";
import { useCommersCardColumns } from "@/hooks/useCommersCardColumns";
import UseGetFlatRowsTable from "@/hooks/useGetFlatRowsTable";
import { useTableDataRangeStore } from "@/store/table.data.range";
import { TableIds } from "@/store/tables";
import { format } from "date-fns";
import { useParams } from "react-router-dom";

export type TransformCardData = {
  label: string;
  data: NumberCeilDTO[] | NullableNumberCeilDTO[];
};

const READABLE_RATING_CARD_LABELS: Record<keyof GetProductRatingHistoryResponseDTO, string> = {
  productCardRating: "Рейтинг",
  productCardReviewsAmount: "Количество отзывов",
  skleikaRating: "Рейтинг в склейке",
  skleikaReviewsAmount: "Количество отзывов в клейке",
};

export const CommersCardComments = () => {
  const dateRange = useTableDataRangeStore((store) => store.dateRange);
  const { id } = useParams<{ id: string }>();
  const productId = Number(id);
  const { data, isLoading, isError, error } = useRatingControllerGetProductRatingHistory(
    productId,
    {
      periodEnd: format(dateRange.to, "yyyy-MM-dd"),
      periodStart: format(dateRange.from, "yyyy-MM-dd"),
    },
    {
      query: {
        queryKey: ["card", "rating", "history", dateRange.from, dateRange.to, productId],
        select: (data) => {
          const keys = Object.keys(data) as (keyof GetProductRatingHistoryResponseDTO)[];

          return keys.map(
            (field): TransformCardData => ({
              label: READABLE_RATING_CARD_LABELS[field] || "Значение не добавлено",
              data: data[field],
            })
          );
        },
      },
    }
  );
  const columns = useCommersCardColumns({ dateRange: dateRange });

  const { table, columnSizeVars, columnOrder } = UseGetFlatRowsTable({
    data: data || [],
    columns,
    tableId: TableIds.Commers_RatingCard,
  });
  if (isError) return <div>{error?.message}</div>;
  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="h-full flex flex-col">
      <TanstackTable style={{ width: table.getTotalSize(), ...columnSizeVars }} className="flex-1">
        {table.getState().columnSizingInfo.isResizingColumn ? (
          <>
            <MemoizedTanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
            <MemoizedTanstackTableBody table={table} columnOrder={columnOrder} />
          </>
        ) : (
          <>
            <DndSortableHeadersContext table={table} tableId={TableIds.Commers_RatingCard}>
              <TanstackTableHeader table={table} columnOrder={columnOrder} sticky="true" />
              <TanstackTableBody table={table} columnOrder={columnOrder} />
            </DndSortableHeadersContext>
          </>
        )}
      </TanstackTable>
    </div>
  );
};
